/**
 * The Hawfinch assistant: a chat panel down the right of every page.
 *
 * The conversation lives here, in the browser, and the whole of it is sent
 * with each question; the server keeps none. It survives moving between
 * pages and, for this tab, a reload.
 *
 * Replies are rendered from a small Markdown subset into React elements,
 * never as HTML. Images are never shown and links are shown as plain text:
 * the assistant reads data typed by the public, and an image or link is the
 * way injected text would try to carry data out.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  ActionIcon, Alert, Box, Button, Collapse, Group, Loader, ScrollArea, Stack, Text, Textarea, Tooltip, UnstyledButton,
} from '@mantine/core';
import {
  IconAlertTriangle, IconChevronDown, IconChevronRight, IconDatabase, IconRefresh, IconSend, IconSparkles, IconX,
} from '@tabler/icons-react';
import { useAssistantChat, useAssistantStatus } from '../api/queries';
import { TOKENS } from '../theme';
import { SpeedChip, type QueryStats } from '../ui';
import { useVocab, type Vocab } from '../vocab';

type Source = { tool: string; label: string; failed: boolean };
export type AssistantTurn = {
  role: 'user' | 'assistant';
  content: string;
  sources?: Source[];
  /** What the lookups behind an answer read, when there were any. */
  query_stats?: QueryStats | null;
  error?: boolean;
};

const STORE = 'hawfinch_assistant_chat';

export function loadConversation(): AssistantTurn[] {
  try {
    const raw = sessionStorage.getItem(STORE);
    return raw ? (JSON.parse(raw) as AssistantTurn[]) : [];
  } catch {
    return [];
  }
}

function saveConversation(turns: AssistantTurn[]) {
  try {
    sessionStorage.setItem(STORE, JSON.stringify(turns.slice(-40)));
  } catch {
    /* private mode */
  }
}

function suggestions(v: Vocab) {
  return [
    `How did each ${v.one('campaign')} perform over the last 7 days?`,
    `Which ${v.many('supplier')} sent the most ${v.many('lead')} this month, and how many ${v.p.sold}?`,
    `What happens when ${v.a('routeGroup')} hits its daily cap?`,
    'How is the Swicc welcome email doing?',
  ];
}

export function AssistantPanel({ turns, setTurns, onClose }: {
  turns: AssistantTurn[];
  setTurns: (t: AssistantTurn[]) => void;
  onClose: () => void;
}) {
  const vocab = useVocab();
  const status = useAssistantStatus(true);
  const chat = useAssistantChat();
  const [text, setText] = useState('');
  const viewport = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { saveConversation(turns); }, [turns]);
  useEffect(() => {
    viewport.current?.scrollTo({ top: viewport.current.scrollHeight, behavior: 'smooth' });
  }, [turns.length, chat.isPending]);
  useEffect(() => { input.current?.focus(); }, []);

  function ask(question = text) {
    const q = question.trim();
    if (!q || chat.isPending) return;
    const next: AssistantTurn[] = [...turns, { role: 'user', content: q }];
    setTurns(next);
    setText('');
    chat.mutate(
      {
        // Failed replies aren't part of the conversation Claude sees.
        messages: next.filter((t) => !t.error).map((t) => ({ role: t.role, content: t.content })),
      },
      {
        onSuccess: (res) => setTurns([
          ...next,
          { role: 'assistant', content: res.reply, sources: res.sources, query_stats: res.query_stats },
        ]),
        onError: (e) => setTurns([...next, { role: 'assistant', content: (e as Error).message, error: true }]),
      },
    );
  }

  const available = status.data?.available;

  return (
    <Stack gap={0} h="100%" style={{ background: TOKENS.surface }}>
      <Group justify="space-between" px="md" py={12} style={{ borderBottom: `1px solid ${TOKENS.border}`, flex: 'none' }}>
        <Group gap={8}>
          <IconSparkles size={17} />
          <Text fw={700} size="sm">Assistant</Text>
          <Text size="xs" c="dimmed">Read-only</Text>
        </Group>
        <Group gap={4}>
          {turns.length > 0 && (
            <Tooltip label="New conversation">
              <ActionIcon variant="subtle" color="gray" size="sm" aria-label="New conversation"
                disabled={chat.isPending} onClick={() => setTurns([])}>
                <IconRefresh size={15} />
              </ActionIcon>
            </Tooltip>
          )}
          <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Close the assistant" onClick={onClose}>
            <IconX size={16} />
          </ActionIcon>
        </Group>
      </Group>

      <ScrollArea style={{ flex: 1 }} viewportRef={viewport}>
        <Stack gap="md" p="md">
          {status.data && !available && (
            <Alert color="gray" variant="light" icon={<IconAlertTriangle size={16} />}>
              <Text size="sm">{status.data.reason}</Text>
            </Alert>
          )}
          {available && turns.length === 0 && (
            <Stack gap="xs">
              <Text size="sm" c="dimmed">
                {`Ask about your ${vocab.many('campaign')}, routing, ${vocab.many('lead')},
                reports or emails. It can look things up and explain them, but it can't change anything.`}
              </Text>
              {suggestions(vocab).map((s) => (
                <Button key={s} variant="default" size="xs" justify="flex-start" fw={500} onClick={() => ask(s)}
                  styles={{ root: { height: 'auto', paddingTop: 7, paddingBottom: 7 }, label: { whiteSpace: 'normal', textAlign: 'left' } }}>
                  {s}
                </Button>
              ))}
            </Stack>
          )}
          {turns.map((t, i) => (t.role === 'user' ? (
            <Box key={i} px="sm" py={8}
              style={{ alignSelf: 'flex-end', maxWidth: '88%', background: TOKENS.surface2, borderRadius: 10 }}>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{t.content}</Text>
            </Box>
          ) : t.error ? (
            <Alert key={i} color="red" variant="light" p="xs"><Text size="sm">{t.content}</Text></Alert>
          ) : (
            <Stack key={i} gap={6}>
              <Markdown text={t.content} />
              {t.sources && t.sources.length > 0 && <Sources sources={t.sources} />}
              <SpeedChip stats={t.query_stats} />
            </Stack>
          )))}
          {chat.isPending && (
            <Group gap={8}>
              <Loader size={14} />
              <Text size="sm" c="dimmed">Looking it up</Text>
            </Group>
          )}
        </Stack>
      </ScrollArea>

      <Box p="sm" style={{ borderTop: `1px solid ${TOKENS.border}`, flex: 'none' }}>
        <Group gap={6} align="flex-end" wrap="nowrap">
          <Textarea
            ref={input}
            style={{ flex: 1 }}
            autosize
            minRows={1}
            maxRows={6}
            placeholder={available ? 'Ask a question' : 'Not available yet'}
            disabled={!available || chat.isPending}
            value={text}
            onChange={(e) => { const v = e.currentTarget.value; setText(v); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); }
            }}
          />
          <ActionIcon size="lg" aria-label="Send" disabled={!available || !text.trim() || chat.isPending} onClick={() => ask()}>
            <IconSend size={16} />
          </ActionIcon>
        </Group>
      </Box>
    </Stack>
  );
}

/** What the assistant looked at, folded away under the answer. */
function Sources({ sources }: { sources: Source[] }) {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <UnstyledButton onClick={() => setOpen((o) => !o)}>
        <Group gap={4}>
          {open ? <IconChevronDown size={13} color="var(--mantine-color-dimmed)" /> : <IconChevronRight size={13} color="var(--mantine-color-dimmed)" />}
          <Text size="xs" c="dimmed">Looked at {sources.length} {sources.length === 1 ? 'thing' : 'things'}</Text>
        </Group>
      </UnstyledButton>
      <Collapse in={open}>
        <Stack gap={2} mt={4} pl={17}>
          {sources.map((s, i) => (
            <Group key={i} gap={6} wrap="nowrap">
              <IconDatabase size={12} color="var(--mantine-color-dimmed)" style={{ flexShrink: 0 }} />
              <Text size="xs" c={s.failed ? 'red' : 'dimmed'}>{s.label}{s.failed ? ' (failed)' : ''}</Text>
            </Group>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// A small Markdown subset, rendered as React elements (never as HTML).
// ---------------------------------------------------------------------------

/** **bold**, `code` and [text](url) shown as text only. */
function inline(s: string): ReactNode[] {
  const out: ReactNode[] = [];
  // Links and images keep only their text.
  const plain = s.replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1');
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(plain))) {
    if (m.index > last) out.push(plain.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) out.push(<b key={k++}>{tok.slice(2, -2)}</b>);
    else out.push(<code key={k++} style={{ fontSize: '0.88em', background: TOKENS.surface2, padding: '1px 4px', borderRadius: 4 }}>{tok.slice(1, -1)}</code>);
    last = m.index + tok.length;
  }
  if (last < plain.length) out.push(plain.slice(last));
  return out;
}

function cells(row: string): string[] {
  return row.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
}

export function Markdown({ text }: { text: string }) {
  const lines = text.replace(/\r/g, '').split('\n');
  const at = (n: number) => lines[n] ?? '';
  const blocks: ReactNode[] = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = at(i);
    if (!line.trim()) { i++; continue; }

    // Table: a header row, a --- row, then rows.
    if (line.trim().startsWith('|') && i + 1 < lines.length && /^\s*\|?\s*:?-{2,}/.test(at(i + 1))) {
      const head = cells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && at(i).trim().startsWith('|')) { rows.push(cells(at(i))); i++; }
      blocks.push(
        <Box key={k++} style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%' }}>
            <thead><tr>{head.map((h, j) => (
              <th key={j} style={{ textAlign: 'left', padding: '4px 8px', borderBottom: `1px solid ${TOKENS.borderStrong}`, whiteSpace: 'nowrap' }}>{inline(h)}</th>
            ))}</tr></thead>
            <tbody>{rows.map((r, ri) => (
              <tr key={ri}>{r.map((c, j) => (
                <td key={j} style={{ padding: '4px 8px', borderBottom: `1px solid ${TOKENS.border}`, fontVariantNumeric: 'tabular-nums' }}>{inline(c)}</td>
              ))}</tr>
            ))}</tbody>
          </table>
        </Box>,
      );
      continue;
    }

    // Lists.
    if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\./.test(line);
      const items: string[] = [];
      while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(at(i))) {
        items.push(at(i).replace(/^\s*([-*]|\d+\.)\s+/, ''));
        i++;
      }
      const ListTag = ordered ? 'ol' : 'ul';
      blocks.push(
        <ListTag key={k++} style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 1.55 }}>
          {items.map((it, j) => <li key={j} style={{ marginBottom: 2 }}>{inline(it)}</li>)}
        </ListTag>,
      );
      continue;
    }

    // Headings become bold lines.
    const h = /^#{1,6}\s+(.*)$/.exec(line);
    if (h) {
      blocks.push(<Text key={k++} fw={700} size="sm">{inline(h[1] ?? '')}</Text>);
      i++;
      continue;
    }

    // Paragraph: consecutive plain lines.
    const para: string[] = [];
    while (i < lines.length && at(i).trim() && !/^\s*([-*]|\d+\.)\s+/.test(at(i)) && !at(i).trim().startsWith('|') && !/^#{1,6}\s/.test(at(i))) {
      para.push(at(i));
      i++;
    }
    blocks.push(<Text key={k++} size="sm" style={{ lineHeight: 1.55 }}>{inline(para.join(' '))}</Text>);
  }
  return <Stack gap={8}>{blocks}</Stack>;
}
